<script lang="ts" setup>
import {
  CrmContact,
  useCrmContactsQuery,
  useCrmContactsWithoutSearchQuery,
} from "@/graphql/operations";
import { computed, ref, shallowRef } from "vue";
import { Search, Tooltip } from "@/components";
import CrmContactInfo from "./CRMContactInfo.vue";
import { refDebounced } from "@vueuse/core";

const search = shallowRef<string>("");
const currentSearch = refDebounced(search, 750, { maxWait: 3 * 750 });
const limit = 100;
const offset = ref<number>(0);

const { data: dataWithSearch, fetching } = useCrmContactsQuery({
  variables: computed(() => ({
    search: currentSearch.value,
    limit: limit,
    offset: offset.value,
  })),
  requestPolicy: "cache-and-network",
  pause: computed(() => currentSearch.value?.trim().length === 0),
});

const { data: dataWithoutSearch, fetching: fetching2 } = useCrmContactsWithoutSearchQuery({
  variables: computed(() => ({
    limit: 30,
    offset: offset.value,
  })),
  requestPolicy: "cache-and-network",
  pause: computed(() => currentSearch.value?.trim().length > 0),
});

const hasNextPage = computed(
  () => currentSearch.value?.trim().length === 0 ?
    dataWithoutSearch.value?.crmContacts?.pageInfo?.hasNextPage :
    dataWithSearch.value?.rankedSearchCrmContactsByName?.pageInfo?.hasNextPage
);
const hasPreviousPage = computed(
  () => currentSearch.value?.trim().length === 0 ?
    dataWithoutSearch.value?.crmContacts?.pageInfo?.hasPreviousPage :
    dataWithSearch.value?.rankedSearchCrmContactsByName?.pageInfo?.hasPreviousPage
);

const crmContacts = computed(() => currentSearch.value?.trim().length === 0 ?
  dataWithoutSearch.value?.crmContacts?.nodes :
  dataWithSearch.value?.rankedSearchCrmContactsByName?.nodes);

const searchStringReset = ref<boolean>(false);
const setSearchString = (str: string) => {
  search.value = str;
};

const goToPreviousPage = () => {
  offset.value = offset.value - limit;
};

const goToNextPage = () => {
  offset.value = offset.value + limit;
};
</script>

<template>
  <div class="page_content">
    <div class="title_container">
      <h3>{{ $t("crmContacts.title") }}</h3>

      <div class="search_container">
        <Search
          :inheritClassicTheme="true"
          :placeholder="$t('crmContactSearch.search.placeholder')"
          :resetValue="searchStringReset"
          @change="setSearchString"
        />
      </div>
    </div>

    <div class="controls">
      <div class="control">
        <tooltip>
          <template v-slot>
            <span
              :class="{ 'material-symbols-outlined': true, disabled: !hasPreviousPage }"
              @click="goToPreviousPage"
            >
              chevron_left
            </span>
          </template>
          <template #tooltip>{{
            $t("crmContacts.pagination.previous")
          }}</template>
        </tooltip>
        <tooltip>
          <template v-slot>
            <span
              :class="{ 'material-symbols-outlined': true, disabled: !hasNextPage }"
              @click="goToNextPage"
            >
              chevron_right
            </span>
          </template>
          <template #tooltip>{{ $t("crmContacts.pagination.next") }}</template>
        </tooltip>
      </div>
    </div>
    <crm-contact-info
      v-for="contact in crmContacts"
      :key="contact!.id"
      :contact="contact! as CrmContact"
    />
  </div>
</template>

<style scoped>
.title_container {
  display: flex;
}

.title_container h3 {
  flex: 1;
  font-size: 1.1rem;
  font-weight: 500;
}

.search_container {
  display: flex;
  align-items: center;
}

.controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.control span {
  cursor: pointer;
  font-size: 1.1rem;
  border-radius: 50%;
  padding: 0.58em;
  line-height: 1;
}

.control span:hover {
  background-color: var(--classic-surface-hover, #f4f4f4);
}

.control span.disabled {
  pointer-events: none;
  cursor: initial;
  opacity: 0.3;
}
</style>
